import React, { useState, useEffect } from 'react';
import { Card, Form, Button, Alert, Spinner, Badge, Table, Toast, ToastContainer } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronLeft, faCheck, faPaperPlane, faExclamationTriangle, faPrint, faList } from '@fortawesome/free-solid-svg-icons';
import '../common.css';
import { generateTourPlan, downloadTourPlanById } from '../../../service/workflow-api';
import Markdown from 'markdown-to-jsx';
import { useHistory } from 'react-router-dom';

// Blue button styles
const blueButtonStyle = {
  backgroundColor: 'rgb(77, 122, 255)',
  borderColor: 'rgb(77, 122, 255)',
  color: '#fff'
};

const blueButtonHoverStyle = {
  backgroundColor: 'rgb(60, 105, 240)',
  borderColor: 'rgb(60, 105, 240)',
  color: '#fff'
};

const ReviewSubmit = ({ formData, onInputChange, onPrev, onSubmit, error, isReviewOnly = false }) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [aiProcessing, setAiProcessing] = useState(false);
  const [aiResponse, setAiResponse] = useState(null);
  const [htmlContent, setHtmlContent] = useState('');
  const [additionalNotes, setAdditionalNotes] = useState(formData.additionalNotes || '');
  const [aiError, setAiError] = useState('');
  const [validationError, setValidationError] = useState('');
  const [showToast, setShowToast] = useState(false);
  const history = useHistory();
  
  // Scroll to top when tour plan is generated
  useEffect(() => {
    if (aiResponse && htmlContent) {
      window.scrollTo(0, 0);
    }
  }, [aiResponse, htmlContent]);
  
  // Validate mandatory fields
  const validateMandatoryFields = () => {
    const mandatoryFields = [
      { field: 'tourName', label: 'Tour Name' },
      { field: 'artistName', label: 'Artist/Band Name' },
      { field: 'tourDuration', label: 'Tour Duration' },
      { field: 'targetRegions', label: 'Target Regions', isArray: true },
      { field: 'venueTypes', label: 'Venue Types', isArray: true },
      { field: 'transportationTypes', label: 'Transportation Types', isArray: true }
    ];
    
    const missingFields = mandatoryFields.filter(item => {
      if (item.isArray) {
        return !formData[item.field] || formData[item.field].length === 0;
      }
      return !formData[item.field];
    }).map(item => item.label);
    
    if (missingFields.length > 0) {
      setValidationError(`Please complete the following mandatory fields: ${missingFields.join(', ')}`);
      setShowToast(true);
      return false;
    }
    
    return true;
  };
  
  // Handle the form submission
  const handleSubmit = async () => {
    console.log('Submitting form data:', formData);
    if (isReviewOnly) {
      // If in review mode, just pass to the parent's submit handler
      onSubmit();
      return;
    }
    
    // First validate the mandatory fields
    if (!validateMandatoryFields()) {
      return;
    }
    
    setIsSubmitting(true);
    setAiProcessing(true);
    setAiError('');
    
    try {
      // Update form data with any additional notes
      const updatedFormData = { ...formData, additionalNotes };
      onInputChange({ additionalNotes });
      
      // Submit the form data via the parent's submit handler
      const response = await onSubmit(updatedFormData);
      
      if (response) {
        // Create response structure for the UI
        const aiResponseObj = {
          planId: response.planId,
          downloadUrl: response.downloadUrl,
          viewUrl: response.viewUrl,
          generatedDate: new Date().toISOString()
        };
        
        setAiResponse(aiResponseObj);
        
        // Fetch the HTML content
        await fetchTourPlanContent(response.planId);
      }
      
      setIsSubmitting(false);
      setAiProcessing(false);
    } catch (err) {
      console.error('Error generating tour plan:', err);
      setAiError('An error occurred while processing your request. Please try again.');
      setIsSubmitting(false);
      setAiProcessing(false);
    }
  };

  // Fetch the tour plan content
  const fetchTourPlanContent = async (planId) => {
    try {
      const response = await downloadTourPlanById(planId);
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      const data = await response.json();
      
      if (data && data.html) {
        setHtmlContent(data.html);
      } else {
        throw new Error('No HTML content received');
      }
    } catch (err) {
      console.error('Error fetching tour plan content:', err);
      setAiError('Successfully generated plan but could not load preview.');
    }
  };
  
  // Handle viewing saved plan in a new window
  const handleViewPlan = () => {
    if (aiResponse && aiResponse.viewUrl) {
      history.push(aiResponse.viewUrl);
    }
  };
  
  // Handle print
  const handlePrint = () => {
    if (!htmlContent) { // htmlContent is from downloadTourPlanById
      alert('No content available to print.');
      return;
    }
    const printWindow = window.open('', '_blank');
    if (!printWindow) {
      alert('Please allow pop-ups to print the tour plan.');
      return;
    }
    printWindow.document.write(htmlContent); // Assumes htmlContent from backend is fully styled for screen and print
    printWindow.document.close();
    // Timeout to allow content to render, especially if there are images or complex CSS
    printWindow.setTimeout(() => {
      printWindow.focus(); // Focus the new window before printing
      try {
        printWindow.print();
      } catch (e) {
        console.error("Print error:", e);
        alert("An error occurred while trying to print. Please try again.");
      }
      // Optional: close the window after printing, but often better to leave it for the user.
      // if (printWindow.close) {
      //   printWindow.close();
      // }
    }, 1000); // 1 second delay, adjust if needed
  };

  // Format summary sections
  const formatSummary = (section) => {
    if (!section) return 'Not provided';
    
    if (Array.isArray(section)) {
      return section.join(', ') || 'None selected';
    }
    
    if (typeof section === 'object') {
      return Object.entries(section)
        .map(([key, value]) => `${key}: ${value}`)
        .join(', ') || 'No data';
    }
    
    return section.toString() || 'Not provided';
  };
  
  // Simple text summary for specific fields
  const getTextSummary = (field, defaultText = 'Not provided') => {
    return formData[field] || defaultText;
  };
  
  // Format date value
  const formatDate = (dateString) => {
    if (!dateString) return 'Not specified';
    
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      });
    } catch (e) {
      return dateString;
    }
  };
  
  // Generate array displays with badges
  const generateBadgeList = (items, variant = 'primary') => {
    if (!items || !Array.isArray(items) || items.length === 0) {
      return <span className="text-muted">None selected</span>;
    }
    
    // Helper function to format badges with proper capitalization
    const formatBadgeText = (text) => {
      // Handle kebab-case IDs 
      if (typeof text === 'string' && text.includes('-')) {
        // Replace hyphens with spaces and capitalize each word
        return text.split('-')
          .map(word => word.charAt(0).toUpperCase() + word.slice(1))
          .join(' ');
      }
      // Handle simple strings
      return typeof text === 'string' 
        ? text.charAt(0).toUpperCase() + text.slice(1) 
        : text;
    };
    
    return items.map((item, index) => (
      <Badge 
        key={index} 
        bg={variant} 
        className="me-2 mb-2"
        style={{ background: variant === 'primary' 
          ? 'linear-gradient(90deg, rgba(145, 105, 247, 0.8) 0%, rgba(113, 71, 219, 0.8) 100%)' 
          : undefined 
        }}
      >
        {formatBadgeText(item)}
      </Badge>
    ));
  };

  // Section header style - transparent with purple shade
  const sectionStyle = {
    background: 'transparent',
    color: 'rgba(190, 140, 255, 0.95)',
    fontSize: '1.4rem',
    padding: '0.5rem 0',
    marginBottom: '1rem',
    fontWeight: 'bold',
    width: '100%',
    display: 'block',
    textAlign: 'left',
    borderBottom: '1px solid rgba(170, 80, 255, 0.4)'
  };

  // Section container style
  const sectionContainerStyle = {
    backgroundColor: 'rgba(40, 40, 60, 0.6)',
    padding: '1.5rem',
    borderRadius: '0.5rem',
    marginBottom: '1.5rem'
  };
  
  // Table styles
  const tableStyle = {
    color: '#fff',
    backgroundColor: 'transparent',
    variant: 'dark'
  };
  
  const tableCellStyle = {
    borderColor: 'rgba(170, 80, 255, 0.2)',
    padding: '0.75rem',
    verticalAlign: 'middle',
    color: '#fff',
    backgroundColor: 'rgba(30, 30, 45, 0.7)'
  };
  
  const tableLabelStyle = {
    fontWeight: 'bold',
    width: '30%',
    backgroundColor: 'rgba(25, 25, 35, 0.85)'
  };

  return (
    <Card className="dark-main-card text-white mb-4">
      <Card.Header className="dark-card-header border-bottom d-flex justify-content-between align-items-center">
        <h4 className="mb-0 text-white">{isReviewOnly ? "Tour Plan Review" : "Review and Generate Tour Plan"}</h4>
        <div>
          {aiResponse && (
            <Button 
              style={blueButtonStyle}
              onMouseOver={e => e.currentTarget.style = {...blueButtonHoverStyle}}
              onMouseOut={e => e.currentTarget.style = {...blueButtonStyle}}
              size="sm" 
              onClick={handlePrint}
              className="me-2"
            >
              <FontAwesomeIcon icon={faPrint} className="me-2" />
              Print
            </Button>
          )}
        </div>
      </Card.Header>
      <Card.Body className="p-4">
        {error && <Alert variant="danger" className="dark-alert mb-4">{error}</Alert>}
        
        {/* Toast notifications for validation errors */}
        <ToastContainer className="p-3" position="top-end">
          <Toast 
            show={showToast} 
            onClose={() => setShowToast(false)} 
            delay={5000} 
            autohide 
            bg="warning"
            style={{ minWidth: '350px' }}
          >
            <Toast.Header>
              <FontAwesomeIcon icon={faExclamationTriangle} className="me-2" />
              <strong className="me-auto">Missing Information</strong>
            </Toast.Header>
            <Toast.Body className="text-dark">{validationError}</Toast.Body>
          </Toast>
        </ToastContainer>
        
        {/* AI Response Section with embedded markdown content */}
        {aiResponse && htmlContent && (
          <Card className="dark-main-card text-white mb-4" style={{
            height: "650px",
          }}>
            <Card.Header className="dark-card-success-header d-flex justify-content-between align-items-center">
              <h5 className="mb-0">Your Tour Plan Is Ready!</h5>
            </Card.Header>
            <Card.Body>
              <p className="text-muted mb-3 small">
                Scroll through your comprehensive tour plan below. Use the Print button to get a physical copy or save as PDF.
              </p>
              <div 
                className="tour-plan-content mb-3" 
                style={{
                  backgroundColor: '#121212', 
                  color: '#e0e0e0', 
                  padding: '20px', 
                  borderRadius: '6px',
                  height: '650px',
                  paddingBottom: '20px',
                  overflowY: 'auto'
                }}
              >
                <div style={{ height: '100%', borderRadius: '6px', overflow: 'hidden' }}>
                  <iframe
                    srcDoc={htmlContent}
                    title="Tour Plan Preview"
                    style={{ 
                      width: '100%', 
                      height: '100%', 
                      border: 'none',
                      backgroundColor: '#121212'
                    }}
                  />
                </div>
              </div>
            </Card.Body>
          </Card>
        )}
        
        {/* Direct display sections replacing accordions */}
        <div className="mb-4">
          {/* Tour Concept & Objectives */}
          <div style={sectionContainerStyle}>
            <div style={sectionStyle}>Tour Concept & Objectives</div>
            <div className="table-responsive">
              <Table borderless responsive style={tableStyle} variant="dark" className="table-dark">
                <tbody>
                  <tr>
                    <td style={{...tableCellStyle, ...tableLabelStyle}}>Tour Name <span className="text-danger">*</span></td>
                    <td style={tableCellStyle}>{getTextSummary('tourName')}</td>
                  </tr>
                  <tr>
                    <td style={{...tableCellStyle, ...tableLabelStyle}}>Artist/Band Name <span className="text-danger">*</span></td>
                    <td style={tableCellStyle}>{getTextSummary('artistName')}</td>
                  </tr>
                  <tr>
                    <td style={{...tableCellStyle, ...tableLabelStyle}}>Genre</td>
                    <td style={tableCellStyle}>{getTextSummary('genre')}</td>
                  </tr>
                  <tr>
                    <td style={{...tableCellStyle, ...tableLabelStyle}}>Tour Scale</td>
                    <td style={tableCellStyle}>
                      {formData.tourScale ? 
                        formData.tourScale.charAt(0).toUpperCase() + formData.tourScale.slice(1) : 
                        'Not specified'}
                    </td>
                  </tr>
                  <tr>
                    <td style={{...tableCellStyle, ...tableLabelStyle}}>Tour Concept</td>
                    <td style={tableCellStyle}>{getTextSummary('tourConcept')}</td>
                  </tr>
                  <tr>
                    <td style={{...tableCellStyle, ...tableLabelStyle}}>Primary Tour Objectives</td>
                    <td style={tableCellStyle}>{generateBadgeList(formData.tourObjectives)}</td>
                  </tr>
                  <tr>
                    <td style={{...tableCellStyle, ...tableLabelStyle}}>Primary Age Range</td>
                    <td style={tableCellStyle}>{getTextSummary('audienceAge')}</td>
                  </tr>
                  <tr>
                    <td style={{...tableCellStyle, ...tableLabelStyle}}>Fanbase Size</td>
                    <td style={tableCellStyle}>{getTextSummary('fanbaseSize')}</td>
                  </tr>
                  <tr>
                    <td style={{...tableCellStyle, ...tableLabelStyle}}>Target Audience</td>
                    <td style={tableCellStyle}>{getTextSummary('audienceDescription')}</td>
                  </tr>
                  <tr>
                    <td style={{...tableCellStyle, ...tableLabelStyle}}>Career Stage</td>
                    <td style={tableCellStyle}>{getTextSummary('careerStage')}</td>
                  </tr>
                  <tr>
                    <td style={{...tableCellStyle, ...tableLabelStyle}}>Release Status</td>
                    <td style={tableCellStyle}>{getTextSummary('releaseStatus')}</td>
                  </tr>
                  <tr>
                    <td style={{...tableCellStyle, ...tableLabelStyle}}>Previous Touring Experience</td>
                    <td style={tableCellStyle}>{getTextSummary('tourExperience')}</td>
                  </tr>
                </tbody>
              </Table>
            </div>
          </div>
          
          {/* Timeline & Geographic Scope */}
          <div style={sectionContainerStyle}>
            <div style={sectionStyle}>Timeline & Geographic Scope</div>
            <div className="table-responsive">
              <Table borderless responsive style={tableStyle} variant="dark" className="table-dark">
                <tbody>
                  <tr>
                    <td style={{...tableCellStyle, ...tableLabelStyle}}>Tour Duration <span className="text-danger">*</span></td>
                    <td style={tableCellStyle}>
                      {formData.tourDuration ? 
                        `${formData.tourDuration} ${formData.tourDurationUnit || 'days'}` : 
                        'Not specified'}
                    </td>
                  </tr>
                  <tr>
                    <td style={{...tableCellStyle, ...tableLabelStyle}}>Preferred Start Date</td>
                    <td style={tableCellStyle}>{formatDate(formData.startDate)}</td>
                  </tr>
                  <tr>
                    <td style={{...tableCellStyle, ...tableLabelStyle}}>Preferred End Date</td>
                    <td style={tableCellStyle}>{formatDate(formData.endDate)}</td>
                  </tr>
                  <tr>
                    <td style={{...tableCellStyle, ...tableLabelStyle}}>Date Flexibility</td>
                    <td style={tableCellStyle}>
                      {formData.dateFlexibility ? 
                        `${formData.dateFlexibility}% flexible` : 
                        'Not specified'}
                    </td>
                  </tr>
                  <tr>
                    <td style={{...tableCellStyle, ...tableLabelStyle}}>Target Regions <span className="text-danger">*</span></td>
                    <td style={tableCellStyle}>{generateBadgeList(formData.targetRegions)}</td>
                  </tr>
                  <tr>
                    <td style={{...tableCellStyle, ...tableLabelStyle}}>Required Cities</td>
                    <td style={tableCellStyle}>{generateBadgeList(formData.specificCities)}</td>
                  </tr>
                  <tr>
                    <td style={{...tableCellStyle, ...tableLabelStyle}}>Regions to Avoid</td>
                    <td style={tableCellStyle}>{generateBadgeList(formData.regionsToAvoid, 'danger')}</td>
                  </tr>
                  <tr>
                    <td style={{...tableCellStyle, ...tableLabelStyle}}>Event Frequency</td>
                    <td style={tableCellStyle}>
                      {formData.eventsPerWeek ? 
                        `${formData.eventsPerWeek} events per week` : 
                        'Not specified'}
                    </td>
                  </tr>
                  <tr>
                    <td style={{...tableCellStyle, ...tableLabelStyle}}>Season Preference</td>
                    <td style={tableCellStyle}>{generateBadgeList(formData.seasonPreferences)}</td>
                  </tr>
                </tbody>
              </Table>
            </div>
          </div>
          
          {/* Budget Parameters */}
          <div style={sectionContainerStyle}>
            <div style={sectionStyle}>Budget Parameters</div>
            <Table borderless responsive style={tableStyle} variant="dark" className="table-dark">
              <tbody>
                <tr>
                  <td style={{...tableCellStyle, ...tableLabelStyle}}>Budget Range</td>
                  <td style={tableCellStyle}>
                    {formData.budgetMin && formData.budgetMax ? 
                      `$${formData.budgetMin} - $${formData.budgetMax}` : 
                      'Not specified'}
                  </td>
                </tr>
                <tr>
                  <td style={{...tableCellStyle, ...tableLabelStyle}}>Budget Flexibility</td>
                  <td style={tableCellStyle}>
                    {formData.budgetFlexibility ? 
                      `${formData.budgetFlexibility}% flexible` : 
                      'Not specified'}
                  </td>
                </tr>
                <tr>
                  <td style={{...tableCellStyle, ...tableLabelStyle}}>Budget Priorities</td>
                  <td style={tableCellStyle}>
                    {formData.budgetPriorities ? 
                      Object.entries(formData.budgetPriorities)
                        .sort((a, b) => a[1] - b[1])
                        .map(([key, value], index) => (
                          <Badge 
                            key={index} 
                            bg="primary" 
                            className="me-2 mb-2"
                            style={{ background: 'linear-gradient(90deg, rgba(145, 105, 247, 0.8) 0%, rgba(113, 71, 219, 0.8) 100%)' }}
                          >
                            {key} (Priority: {value})
                          </Badge>
                        )) : 
                      <span className="text-muted">None specified</span>}
                  </td>
                </tr>
                <tr>
                  <td style={{...tableCellStyle, ...tableLabelStyle}}>Revenue Sources</td>
                  <td style={tableCellStyle}>{generateBadgeList(formData.revenueSources)}</td>
                </tr>
                <tr>
                  <td style={{...tableCellStyle, ...tableLabelStyle}}>Revenue Target Per Location</td>
                  <td style={tableCellStyle}>
                    {formData.revenueTarget ? 
                      `$${formData.revenueTarget}` : 
                      'Not specified'}
                  </td>
                </tr>
                <tr>
                  <td style={{...tableCellStyle, ...tableLabelStyle}}>Break-Even Requirement</td>
                  <td style={tableCellStyle}>
                    {formData.breakEvenRequirement === 'yes' ? 
                      'Must break even' : 
                      formData.breakEvenRequirement === 'no' ? 
                      'Can operate at a loss' :
                      'Not specified'}
                  </td>
                </tr>
                <tr>
                  <td style={{...tableCellStyle, ...tableLabelStyle}}>Maximum Per-Event Budget</td>
                  <td style={tableCellStyle}>
                    {formData.maxPerEventBudget ? 
                      `$${formData.maxPerEventBudget}` : 
                      'Not specified'}
                  </td>
                </tr>
                <tr>
                  <td style={{...tableCellStyle, ...tableLabelStyle}}>Maximum Venue Cost</td>
                  <td style={tableCellStyle}>
                    {formData.maxVenueCost ? 
                      `$${formData.maxVenueCost}` : 
                      'Not specified'}
                  </td>
                </tr>
                <tr>
                  <td style={{...tableCellStyle, ...tableLabelStyle}}>Maximum Per-Person Cost</td>
                  <td style={tableCellStyle}>
                    {formData.maxPerPersonCost ? 
                      `$${formData.maxPerPersonCost}` : 
                      'Not specified'}
                  </td>
                </tr>
                <tr>
                  <td style={{...tableCellStyle, ...tableLabelStyle}}>Other Cost Limitations</td>
                  <td style={tableCellStyle}>{getTextSummary('costLimitations')}</td>
                </tr>
              </tbody>
            </Table>
          </div>
          
          {/* Venue & Technical Requirements */}
          <div style={sectionContainerStyle}>
            <div style={sectionStyle}>Venue & Technical Requirements</div>
            <Table borderless responsive style={tableStyle} variant="dark" className="table-dark">
              <tbody>
                <tr>
                  <td style={{...tableCellStyle, ...tableLabelStyle}}>Venue Types <span className="text-danger">*</span></td>
                  <td style={tableCellStyle}>{generateBadgeList(formData.venueTypes)}</td>
                </tr>
                <tr>
                  <td style={{...tableCellStyle, ...tableLabelStyle}}>Capacity Range</td>
                  <td style={tableCellStyle}>
                    {formData.minCapacity && formData.maxCapacity ? 
                      `${formData.minCapacity} - ${formData.maxCapacity} people` : 
                      'Not specified'}
                  </td>
                </tr>
                <tr>
                  <td style={{...tableCellStyle, ...tableLabelStyle}}>Main Space Size</td>
                  <td style={tableCellStyle}>
                    {formData.mainSpaceSize ? 
                      `${formData.mainSpaceSize} ${formData.mainSpaceSizeUnit || 'sqft'}` : 
                      'Not specified'}
                  </td>
                </tr>
                <tr>
                  <td style={{...tableCellStyle, ...tableLabelStyle}}>Seating Styles</td>
                  <td style={tableCellStyle}>{generateBadgeList(formData.seatingStyles)}</td>
                </tr>
                <tr>
                  <td style={{...tableCellStyle, ...tableLabelStyle}}>Sound Requirements</td>
                  <td style={tableCellStyle}>{getTextSummary('technical_sound')}</td>
                </tr>
                <tr>
                  <td style={{...tableCellStyle, ...tableLabelStyle}}>Lighting Requirements</td>
                  <td style={tableCellStyle}>{getTextSummary('technical_lighting')}</td>
                </tr>
                <tr>
                  <td style={{...tableCellStyle, ...tableLabelStyle}}>Stage Requirements</td>
                  <td style={tableCellStyle}>{getTextSummary('technical_stage')}</td>
                </tr>
                <tr>
                  <td style={{...tableCellStyle, ...tableLabelStyle}}>Video/Projection Needs</td>
                  <td style={tableCellStyle}>{getTextSummary('technical_video')}</td>
                </tr>
                <tr>
                  <td style={{...tableCellStyle, ...tableLabelStyle}}>Internet/Connectivity</td>
                  <td style={tableCellStyle}>{getTextSummary('technical_internet')}</td>
                </tr>
                <tr>
                  <td style={{...tableCellStyle, ...tableLabelStyle}}>Power Requirements</td>
                  <td style={tableCellStyle}>{getTextSummary('technical_power')}</td>
                </tr>
                <tr>
                  <td style={{...tableCellStyle, ...tableLabelStyle}}>Other Technical Needs</td>
                  <td style={tableCellStyle}>{getTextSummary('technical_other')}</td>
                </tr>
                <tr>
                  <td style={{...tableCellStyle, ...tableLabelStyle}}>Additional Spaces Needed</td>
                  <td style={tableCellStyle}>{generateBadgeList(formData.additionalSpaces)}</td>
                </tr>
                <tr>
                  <td style={{...tableCellStyle, ...tableLabelStyle}}>Venue Amenities</td>
                  <td style={tableCellStyle}>{generateBadgeList(formData.venueAmenities)}</td>
                </tr>
                <tr>
                  <td style={{...tableCellStyle, ...tableLabelStyle}}>Venue Exclusions</td>
                  <td style={tableCellStyle}>{getTextSummary('venueExclusions')}</td>
                </tr>
              </tbody>
            </Table>
          </div>
          
          {/* Logistics & Transportation */}
          <div style={sectionContainerStyle}>
            <div style={sectionStyle}>Logistics & Transportation</div>
            <Table borderless responsive style={tableStyle} variant="dark" className="table-dark">
              <tbody>
                <tr>
                  <td style={{...tableCellStyle, ...tableLabelStyle}}>Transportation Types <span className="text-danger">*</span></td>
                  <td style={tableCellStyle}>{generateBadgeList(formData.transportationTypes)}</td>
                </tr>
                <tr>
                  <td style={{...tableCellStyle, ...tableLabelStyle}}>Team Size</td>
                  <td style={tableCellStyle}>
                    {formData.coreTeamSize ? 
                      `${formData.coreTeamSize} core members + ${formData.additionalPersonnel || '0'} additional personnel` : 
                      'Not specified'}
                  </td>
                </tr>
                <tr>
                  <td style={{...tableCellStyle, ...tableLabelStyle}}>Equipment Volume</td>
                  <td style={tableCellStyle}>{getTextSummary('equipmentVolume')}</td>
                </tr>
                <tr>
                  <td style={{...tableCellStyle, ...tableLabelStyle}}>Special Handling Items</td>
                  <td style={tableCellStyle}>{getTextSummary('specialHandlingEquipment')}</td>
                </tr>
                <tr>
                  <td style={{...tableCellStyle, ...tableLabelStyle}}>Oversized Items</td>
                  <td style={tableCellStyle}>{getTextSummary('oversizedItems')}</td>
                </tr>
                <tr>
                  <td style={{...tableCellStyle, ...tableLabelStyle}}>Fragile/Sensitive Items</td>
                  <td style={tableCellStyle}>{getTextSummary('fragileItems')}</td>
                </tr>
                <tr>
                  <td style={{...tableCellStyle, ...tableLabelStyle}}>Transportation Permits Needed</td>
                  <td style={tableCellStyle}>{getTextSummary('transportationPermits')}</td>
                </tr>
                <tr>
                  <td style={{...tableCellStyle, ...tableLabelStyle}}>Accommodation Needs</td>
                  <td style={tableCellStyle}>
                    {formData.standardRooms || formData.upgradeRooms ? 
                      `${formData.standardRooms || '0'} standard rooms + ${formData.upgradeRooms || '0'} suites/upgraded rooms` : 
                      'Not specified'}
                  </td>
                </tr>
                <tr>
                  <td style={{...tableCellStyle, ...tableLabelStyle}}>Special Room Requirements</td>
                  <td style={tableCellStyle}>{getTextSummary('specialRoomRequirements')}</td>
                </tr>
                <tr>
                  <td style={{...tableCellStyle, ...tableLabelStyle}}>Accommodation Level</td>
                  <td style={tableCellStyle}>
                    {formData.accommodationLevel ? 
                      `${formData.accommodationLevel <= 25 ? 'Economy' : 
                        formData.accommodationLevel <= 50 ? 'Mid-range' : 
                        formData.accommodationLevel <= 75 ? 'Premium' : 'Luxury'}` : 
                      'Not specified'}
                  </td>
                </tr>
                <tr>
                  <td style={{...tableCellStyle, ...tableLabelStyle}}>Distance from Venue</td>
                  <td style={tableCellStyle}>
                    {formData.venueDistance ? 
                      `Within ${formData.venueDistance} ${formData.venueDistanceUnit || 'minutes'} of venue` : 
                      'Not specified'}
                  </td>
                </tr>
                <tr>
                  <td style={{...tableCellStyle, ...tableLabelStyle}}>Special Logistical Considerations</td>
                  <td style={tableCellStyle}>{getTextSummary('specialLogistics')}</td>
                </tr>
              </tbody>
            </Table>
          </div>
          
          {/* Marketing & Promotion */}
          <div style={sectionContainerStyle}>
            <div style={sectionStyle}>Marketing & Promotion</div>
            <Table borderless responsive style={tableStyle} variant="dark" className="table-dark">
              <tbody>
                <tr>
                  <td style={{...tableCellStyle, ...tableLabelStyle}}>Marketing Start Date</td>
                  <td style={tableCellStyle}>{formatDate(formData.marketingStartDate)}</td>
                </tr>
                <tr>
                  <td style={{...tableCellStyle, ...tableLabelStyle}}>Announcement Date</td>
                  <td style={tableCellStyle}>{formatDate(formData.announcementDate)}</td>
                </tr>
                <tr>
                  <td style={{...tableCellStyle, ...tableLabelStyle}}>Ticket Launch Date</td>
                  <td style={tableCellStyle}>{formatDate(formData.ticketLaunchDate)}</td>
                </tr>
                <tr>
                  <td style={{...tableCellStyle, ...tableLabelStyle}}>Marketing Channels</td>
                  <td style={tableCellStyle}>{generateBadgeList(formData.marketingChannels)}</td>
                </tr>
                <tr>
                  <td style={{...tableCellStyle, ...tableLabelStyle}}>Channel Priority</td>
                  <td style={tableCellStyle}>
                    {formData.channelRatings ? 
                      Object.entries(formData.channelRatings)
                        .sort((a, b) => b[1] - a[1])
                        .map(([channel, rating], index) => (
                          <Badge 
                            key={index} 
                            bg="primary" 
                            className="me-2 mb-2"
                            style={{ background: 'linear-gradient(90deg, rgba(145, 105, 247, 0.8) 0%, rgba(113, 71, 219, 0.8) 100%)' }}
                          >
                            {channel} (Rating: {rating}/5)
                          </Badge>
                        )) : 
                      <span className="text-muted">No ratings provided</span>}
                  </td>
                </tr>
                <tr>
                  <td style={{...tableCellStyle, ...tableLabelStyle}}>Social Platforms</td>
                  <td style={tableCellStyle}>{generateBadgeList(formData.socialPlatforms)}</td>
                </tr>
                <tr>
                  <td style={{...tableCellStyle, ...tableLabelStyle}}>Social Media Followers</td>
                  <td style={tableCellStyle}>
                    {formData.socialFollowers ? 
                      `${formData.socialFollowers} followers` : 
                      'Not specified'}
                  </td>
                </tr>
                <tr>
                  <td style={{...tableCellStyle, ...tableLabelStyle}}>Email Subscribers</td>
                  <td style={tableCellStyle}>
                    {formData.emailSubscribers ? 
                      `${formData.emailSubscribers} subscribers` : 
                      'Not specified'}
                  </td>
                </tr>
                <tr>
                  <td style={{...tableCellStyle, ...tableLabelStyle}}>Promotional Assets</td>
                  <td style={tableCellStyle}>{generateBadgeList(formData.promotionalAssets)}</td>
                </tr>
                <tr>
                  <td style={{...tableCellStyle, ...tableLabelStyle}}>Local Ad Budget</td>
                  <td style={tableCellStyle}>
                    {formData.localAdBudget ? 
                      `$${formData.localAdBudget} per location` : 
                      'Not specified'}
                  </td>
                </tr>
                <tr>
                  <td style={{...tableCellStyle, ...tableLabelStyle}}>Local Partnerships</td>
                  <td style={tableCellStyle}>{formData.localPartnerships ? 'Desired' : 'Not required'}</td>
                </tr>
                <tr>
                  <td style={{...tableCellStyle, ...tableLabelStyle}}>Media Appearances</td>
                  <td style={tableCellStyle}>{formData.mediaAppearances ? 'Desired' : 'Not required'}</td>
                </tr>
                <tr>
                  <td style={{...tableCellStyle, ...tableLabelStyle}}>Pre-Event Promotions</td>
                  <td style={tableCellStyle}>
                    {formData.promoEvents ? 
                      formData.promoEventsDetails || 'Planned (no details provided)' : 
                      'Not planned'}
                  </td>
                </tr>
                <tr>
                  <td style={{...tableCellStyle, ...tableLabelStyle}}>Additional Marketing Notes</td>
                  <td style={tableCellStyle}>{getTextSummary('additionalMarketingNotes')}</td>
                </tr>
              </tbody>
            </Table>
          </div>
          
          {/* Personnel & Staffing */}
          <div style={sectionContainerStyle}>
            <div style={sectionStyle}>Personnel & Staffing</div>
            <Table borderless responsive style={tableStyle} variant="dark" className="table-dark">
              <tbody>
                <tr>
                  <td style={{...tableCellStyle, ...tableLabelStyle}}>Key Staff Positions</td>
                  <td style={tableCellStyle}>
                    {formData.keyStaff && formData.keyStaff.length > 0 ? 
                      formData.keyStaff.map((staff, index) => (
                        <Badge 
                          key={index} 
                          bg="primary" 
                          className="me-2 mb-2"
                          style={{ background: 'linear-gradient(90deg, rgba(145, 105, 247, 0.8) 0%, rgba(113, 71, 219, 0.8) 100%)' }}
                        >
                          {staff.roleLabel} ({staff.count})
                        </Badge>
                      )) : 
                      <span className="text-muted">None added</span>}
                  </td>
                </tr>
                <tr>
                  <td style={{...tableCellStyle, ...tableLabelStyle}}>Hiring Sources</td>
                  <td style={tableCellStyle}>{generateBadgeList(formData.hiringSources)}</td>
                </tr>
                <tr>
                  <td style={{...tableCellStyle, ...tableLabelStyle}}>Specialized Skills Required</td>
                  <td style={tableCellStyle}>{getTextSummary('specializedSkills')}</td>
                </tr>
                <tr>
                  <td style={{...tableCellStyle, ...tableLabelStyle}}>Required Certifications</td>
                  <td style={tableCellStyle}>{generateBadgeList(formData.requiredCertifications)}</td>
                </tr>
                <tr>
                  <td style={{...tableCellStyle, ...tableLabelStyle}}>Other Certifications</td>
                  <td style={tableCellStyle}>{getTextSummary('otherCertification')}</td>
                </tr>
                <tr>
                  <td style={{...tableCellStyle, ...tableLabelStyle}}>Pre-Tour Training</td>
                  <td style={tableCellStyle}>{getTextSummary('preTourTraining')}</td>
                </tr>
                <tr>
                  <td style={{...tableCellStyle, ...tableLabelStyle}}>Management Structure</td>
                  <td style={tableCellStyle}>
                    {formData.managementStructure ? 
                      formData.managementStructure.charAt(0).toUpperCase() + formData.managementStructure.slice(1) : 
                      'Not specified'}
                  </td>
                </tr>
                <tr>
                  <td style={{...tableCellStyle, ...tableLabelStyle}}>Communication Methods</td>
                  <td style={tableCellStyle}>
                    {[
                      formData.dailyBriefings ? 'Daily briefings' : null,
                      formData.digitalPlatform ? `Digital platform (${formData.digitalPlatformName || 'unspecified'})` : null,
                      formData.staffMeetings ? 'Regular staff meetings' : null
                    ].filter(Boolean).join(', ') || 'Not specified'}
                  </td>
                </tr>
                <tr>
                  <td style={{...tableCellStyle, ...tableLabelStyle}}>Additional Personnel Notes</td>
                  <td style={tableCellStyle}>{getTextSummary('additionalPersonnelNotes')}</td>
                </tr>
              </tbody>
            </Table>
          </div>
          
          {/* Compliance & Risk Management Section - New section */}
          <div style={sectionContainerStyle}>
            <div style={sectionStyle}>Compliance & Risk Management</div>
            <Table borderless responsive style={tableStyle} variant="dark" className="table-dark">
              <tbody>
                <tr>
                  <td style={{...tableCellStyle, ...tableLabelStyle}}>Insurance Requirements</td>
                  <td style={tableCellStyle}>{generateBadgeList(formData.insuranceNeeds)}</td>
                </tr>
                <tr>
                  <td style={{...tableCellStyle, ...tableLabelStyle}}>Permits & Licenses</td>
                  <td style={tableCellStyle}>{generateBadgeList(formData.permitsNeeded)}</td>
                </tr>
                <tr>
                  <td style={{...tableCellStyle, ...tableLabelStyle}}>Emergency Response Plan</td>
                  <td style={tableCellStyle}>
                    {formData.hasEmergencyPlan ? 
                      formData.emergencyPlanDetails || 'Required (no details provided)' : 
                      'Not required'}
                  </td>
                </tr>
                <tr>
                  <td style={{...tableCellStyle, ...tableLabelStyle}}>Security Requirements</td>
                  <td style={tableCellStyle}>
                    {formData.hasSecurityRequirements ? 
                      formData.securityRequirements || 'Required (no details provided)' : 
                      'Not required'}
                  </td>
                </tr>
                <tr>
                  <td style={{...tableCellStyle, ...tableLabelStyle}}>Additional Compliance Notes</td>
                  <td style={tableCellStyle}>{getTextSummary('complianceNotes')}</td>
                </tr>
              </tbody>
            </Table>
          </div>
          
          {/* Special Requirements Section - New section */}
          <div style={sectionContainerStyle}>
            <div style={sectionStyle}>Special Requirements</div>
            <Table borderless responsive style={tableStyle} variant="dark" className="table-dark">
              <tbody>
                <tr>
                  <td style={{...tableCellStyle, ...tableLabelStyle}}>VIP Accommodations</td>
                  <td style={tableCellStyle}>
                    {formData.hasVipNeeds ? 
                      formData.vipNeedsDetails || 'Required (no details provided)' : 
                      'Not required'}
                  </td>
                </tr>
                <tr>
                  <td style={{...tableCellStyle, ...tableLabelStyle}}>Special Guest Requirements</td>
                  <td style={tableCellStyle}>{getTextSummary('specialGuestRequirements')}</td>
                </tr>
                <tr>
                  <td style={{...tableCellStyle, ...tableLabelStyle}}>Dietary Requirements</td>
                  <td style={tableCellStyle}>{generateBadgeList(formData.dietaryNeeds)}</td>
                </tr>
                <tr>
                  <td style={{...tableCellStyle, ...tableLabelStyle}}>Catering Notes</td>
                  <td style={tableCellStyle}>{getTextSummary('cateringNotes')}</td>
                </tr>
                <tr>
                  <td style={{...tableCellStyle, ...tableLabelStyle}}>Accessibility Requirements</td>
                  <td style={tableCellStyle}>{generateBadgeList(formData.accessibilityNeeds)}</td>
                </tr>
                <tr>
                  <td style={{...tableCellStyle, ...tableLabelStyle}}>Accessibility Notes</td>
                  <td style={tableCellStyle}>{getTextSummary('accessibilityNotes')}</td>
                </tr>
                <tr>
                  <td style={{...tableCellStyle, ...tableLabelStyle}}>Other Special Requirements</td>
                  <td style={tableCellStyle}>{getTextSummary('otherSpecialRequirements')}</td>
                </tr>
              </tbody>
            </Table>
          </div>
          
          {/* Data Collection Section - New section */}
          <div style={sectionContainerStyle}>
            <div style={sectionStyle}>Data Collection & Analysis</div>
            <Table borderless responsive style={tableStyle} variant="dark" className="table-dark">
              <tbody>
                <tr>
                  <td style={{...tableCellStyle, ...tableLabelStyle}}>Data Sources</td>
                  <td style={tableCellStyle}>{generateBadgeList(formData.dataSources)}</td>
                </tr>
                <tr>
                  <td style={{...tableCellStyle, ...tableLabelStyle}}>Custom Data File</td>
                  <td style={tableCellStyle}>{getTextSummary('customDataFile')}</td>
                </tr>
                <tr>
                  <td style={{...tableCellStyle, ...tableLabelStyle}}>Data Description</td>
                  <td style={tableCellStyle}>{getTextSummary('customDataDescription')}</td>
                </tr>
                <tr>
                  <td style={{...tableCellStyle, ...tableLabelStyle}}>Analysis Priority</td>
                  <td style={tableCellStyle}>
                    {formData.dataPriority ? 
                      formData.dataPriority.charAt(0).toUpperCase() + formData.dataPriority.slice(1) : 
                      'Not specified'}
                  </td>
                </tr>
                <tr>
                  <td style={{...tableCellStyle, ...tableLabelStyle}}>Analysis Depth</td>
                  <td style={tableCellStyle}>
                    {formData.analysisDepth ? 
                      `${formData.analysisDepth}/5 (${formData.analysisDepth <= 2 ? 'Basic' : 
                        formData.analysisDepth <= 3 ? 'Standard' : 'Comprehensive'})` : 
                      'Not specified'}
                  </td>
                </tr>
                <tr>
                  <td style={{...tableCellStyle, ...tableLabelStyle}}>Analysis Notes</td>
                  <td style={tableCellStyle}>{getTextSummary('dataAnalysisNotes')}</td>
                </tr>
              </tbody>
            </Table>
          </div>
        </div>
        
        {/* Additional Notes - only editable when not in review mode */}
        {!isReviewOnly && (
          <Form.Group className="mb-4">
            <Form.Label className="text-white">Additional Notes or Requirements</Form.Label>
            <Form.Control
              as="textarea"
              rows={4}
              className="dark-input"
              placeholder="Add any other details, requirements, or considerations that weren't covered in the form"
              value={additionalNotes}
              onChange={(e) => setAdditionalNotes(e.target.value)}
            />
          </Form.Group>
        )}
        
        {/* Show notes in read-only mode when in review */}
        {isReviewOnly && formData.additionalNotes && (
          <Form.Group className="mb-4">
            <Form.Label className="text-white">Additional Notes</Form.Label>
            <div className="dark-input p-3" style={{ minHeight: '100px', borderRadius: '4px' }}>
              {formData.additionalNotes}
            </div>
          </Form.Group>
        )}
        
        {/* Error Message */}
        {aiError && <Alert variant="danger" className="dark-alert mb-4">{aiError}</Alert>}
        
        {/* Buttons */}
        <div className="d-flex justify-content-between">
          <Button
            style={blueButtonStyle}
            onMouseOver={e => e.currentTarget.style = {...blueButtonHoverStyle}}
            onMouseOut={e => e.currentTarget.style = {...blueButtonStyle}}
            onClick={onPrev}
            disabled={isSubmitting}
          >
            <FontAwesomeIcon icon={faChevronLeft} className="me-2" />
            {isReviewOnly ? "Close" : "Back"}
          </Button>
          
          {!isReviewOnly && (
            <Button
              style={blueButtonStyle}
              onMouseOver={e => e.currentTarget.style = {...blueButtonHoverStyle}}
              onMouseOut={e => e.currentTarget.style = {...blueButtonStyle}}
              onClick={handleSubmit}
              disabled={isSubmitting || !validateMandatoryFields()}
            >
              {isSubmitting ? (
                <>
                  <Spinner
                    as="span"
                    animation="border"
                    size="sm"
                    role="status"
                    aria-hidden="true"
                    className="me-2"
                  />
                  Generating Tour Plan...
                </>
              ) : aiResponse ? (
                <>
                  <FontAwesomeIcon icon={faCheck} className="me-2" />
                  Tour Plan Generated
                </>
              ) : (
                <>
                  <FontAwesomeIcon icon={faPaperPlane} className="me-2" />
                  Generate Tour Plan
                </>
              )}
            </Button>
          )}
          
          {isReviewOnly && (
            <Button
              style={blueButtonStyle}
              onMouseOver={e => e.currentTarget.style = {...blueButtonHoverStyle}}
              onMouseOut={e => e.currentTarget.style = {...blueButtonStyle}}
              onClick={handleSubmit}
              disabled={!validateMandatoryFields()}
            >
              <FontAwesomeIcon icon={faPaperPlane} className="me-2" />
              Generate Tour Plan
            </Button>
          )}
        </div>
        
        {/* AI Processing indicator */}
        {aiProcessing && !aiResponse && !isReviewOnly && (
          <div className="text-center mt-4">
            <Spinner animation="border" role="status" variant="light">
              <span className="visually-hidden">Processing...</span>
            </Spinner>
            <p className="text-light mt-3">
              Our AI is analyzing your requirements and generating a comprehensive tour plan.<br />
              This may take a minute or two.
            </p>
          </div>
        )}
      </Card.Body>
    </Card>
  );
};

export default ReviewSubmit;